class ProductFamilyAjax {
  constructor() {
    this.sectionSelector = '[data-main-product-section]';
    this.swatchSelector = 'input[data-ajax-product-url], input[data-product-url]';
    this.loadingClass = 'is-loading-product-family';
    this.cache = new Map();

    this.bindEvents();
    this.bindPopState();
    this.initCurrentSection(document);
  }

  bindEvents() {
    document.addEventListener('change', (event) => {
      const input = event.target.closest(this.swatchSelector);
      if (!input) return;

      const url = input.getAttribute('data-ajax-product-url') || input.getAttribute('data-product-url');
      if (!url) return;

      if (input.getAttribute('aria-current') === 'true') return;

      event.preventDefault();
      this.loadProduct(url, true);
    });

    document.addEventListener('mouseover', (event) => {
      const input = event.target.closest(this.swatchSelector);
      if (!input) return;

      const url = input.getAttribute('data-ajax-product-url') || input.getAttribute('data-product-url');
      if (url) this.fetchHtml(url).catch(() => {});
    });

    document.addEventListener('click', (event) => {
      const switchBtn = event.target.closest('[data-custom-size-box] .unit-switch');
      if (!switchBtn) return;

      const section = switchBtn.closest(this.sectionSelector) || document;
      this.toggleCustomUnit(switchBtn, section);
    });

    document.addEventListener('focusin', (event) => {
      const field = event.target.closest('[data-custom-size-box] select, [data-custom-size-box] input');
      if (!field) return;

      const label = field.closest('label');
      const desc = label ? label.getAttribute('data-description') : '';
      const optionName = field.getAttribute('data-option');
      const section = field.closest(this.sectionSelector) || document;
      const descBox = section.querySelector('[data-customize-option-desc]');

      if (descBox && optionName && desc) {
        descBox.innerHTML = `<span>${optionName}<span class="required">*</span>: </span>${desc}`;
      }
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest(
        'input[data-custom-size-option], input[data-erp-size-value], select[data-option-name], [data-custom-size-box] select'
      );

      if (!input) return;

      const section = input.closest(this.sectionSelector) || document;

      this.toggleCustomSizeFields(section);
      this.syncErpSizeProperty(section);
      this.updateOptionLabels(section);

      const errorBox = section.querySelector('[data-error-message]');
      if (errorBox) errorBox.innerHTML = '';

      input.classList.remove('is-error');
    });

    document.addEventListener(
      'submit',
      (event) => {
        const form = event.target.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const section = form.closest(this.sectionSelector) || document;

        this.syncErpSizeProperty(section);

        if (!this.validateAndGenerateCustomSize(section)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return false;
        }
      },
      true
    );
  }

  bindPopState() {
    window.addEventListener('popstate', () => {
      this.loadProduct(window.location.pathname + window.location.search, false);
    });
  }

  initCurrentSection(root = document) {
    const section = root.querySelector
      ? root.querySelector(this.sectionSelector) || root
      : document;

    this.initCustomSizeUnit(section);
    this.toggleCustomSizeFields(section);
    this.syncErpSizeProperty(section);
    this.updateOptionLabels(section);
    this.initDeliveryTime(section);
  }

  async loadProduct(url, pushState = true) {
    const currentSection = document.querySelector(this.sectionSelector);

    if (!currentSection) {
      window.location.href = url;
      return;
    }

    const selectedOptions = pushState ? this.getSelectedProductOptions(currentSection) : {};

    try {
      document.documentElement.classList.add(this.loadingClass);

      const htmlText = await this.fetchHtml(url);
      const newDoc = new DOMParser().parseFromString(htmlText, 'text/html');
      const newSection = newDoc.querySelector(this.sectionSelector);

      if (!newSection) {
        window.location.href = url;
        return;
      }

      currentSection.replaceWith(newSection);

      if (pushState) {
        this.restoreSelectedProductOptions(newSection, selectedOptions);
      }

      this.updateDocumentMeta(newDoc);

      if (pushState) {
        const newUrl = new URL(url, window.location.origin);
        window.history.pushState({}, '', newUrl.pathname + newUrl.search);
      }

      this.afterReplace(newSection);
    } catch (error) {
      console.error('Sibling product switch failed:', error);
      window.location.href = url;
    } finally {
      document.documentElement.classList.remove(this.loadingClass);
    }
  }

  async fetchHtml(url) {
    const absoluteUrl = new URL(url, window.location.origin);
    const cacheKey = absoluteUrl.pathname + absoluteUrl.search;

    if (this.cache.has(cacheKey)) return this.cache.get(cacheKey);

    const response = await fetch(absoluteUrl.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

    const html = await response.text();
    this.cache.set(cacheKey, html);

    return html;
  }

  initCustomSizeUnit(root = document) {
    const container = root.querySelector ? root : document;
    const customBox = container.querySelector('[data-custom-size-box]');
    if (!customBox) return;

    const switchBtn = customBox.querySelector('.unit-switch');

    const currentUnit =
      switchBtn && switchBtn.classList.contains('right')
        ? 'cm'
        : 'inch';

    this.rebuildCustomSizeSelects(customBox, currentUnit);

    customBox.querySelectorAll('.unit_item').forEach((item) => {
      if (item.firstChild) {
        item.firstChild.nodeValue = currentUnit === 'cm' ? '(cm)' : '(in)';
      }
    });
  }

  afterReplace(newSection) {
    this.initCurrentSection(newSection);

    document.dispatchEvent(
      new CustomEvent('product:ajax-loaded', {
        detail: {
          section: newSection,
          url: window.location.href
        }
      })
    );

    if (window.Shopify && window.Shopify.PaymentButton) {
      window.Shopify.PaymentButton.init();
    }

    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: window.location.pathname,
        page_title: document.title
      });
    }
  }

  getSelectedProductOptions(section) {
    const selected = {};
    const picker = section.querySelector('variant-radios, variant-selects');

    if (!picker) return selected;

    picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      const name = input.getAttribute('name');
      const value = input.value;

      if (!name || !value) return;
      if (this.shouldIgnoreOptionName(name)) return;

      selected[name] = value;
    });

    picker.querySelectorAll('select').forEach((select) => {
      let name = select.getAttribute('name') || select.getAttribute('data-option-name');
      const value = select.value;

      if (!name || !value) return;

      const match = name.match(/^options\[(.*)\]$/);
      if (match && match[1]) name = match[1];

      if (this.shouldIgnoreOptionName(name)) return;

      selected[name] = value;
    });

    return selected;
  }

  restoreSelectedProductOptions(section, selectedOptions) {
    if (!selectedOptions || Object.keys(selectedOptions).length === 0) return;

    const picker = section.querySelector('variant-radios, variant-selects');
    if (!picker) return;

    Object.keys(selectedOptions).forEach((name) => {
      const value = selectedOptions[name];

      const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
      const radio = radios.find((item) => {
        return item.getAttribute('name') === name && item.value === value && !item.disabled;
      });

      if (radio) {
        radio.checked = true;
        return;
      }

      const selects = Array.from(picker.querySelectorAll('select'));
      const select = selects.find((item) => {
        const rawName = item.getAttribute('name') || item.getAttribute('data-option-name') || '';
        const match = rawName.match(/^options\[(.*)\]$/);
        const normalizedName = match && match[1] ? match[1] : rawName;

        return normalizedName === name;
      });

      if (select) {
        const option = Array.from(select.options).find((item) => item.value === value && !item.disabled);
        if (option) select.value = value;
      }
    });

    picker.dispatchEvent(new Event('change', { bubbles: true }));

    this.toggleCustomSizeFields(section);
    this.syncErpSizeProperty(section);
    this.updateOptionLabels(section);
  }

  syncErpSizeProperty(root = document) {
    const container = root.querySelector ? root : document;
    const erpInput = container.querySelector('[data-erp-size-input]');

    if (!erpInput) return;

    if (this.isCustomSizeSelected(container)) {
      erpInput.value = 'Custom Size';
      return;
    }

    const checkedSizeInput = container.querySelector('input[data-erp-size-value]:checked');

    if (checkedSizeInput) {
      erpInput.value = checkedSizeInput.getAttribute('data-erp-size-value') || '';
      return;
    }

    const sizeSelect = Array.from(container.querySelectorAll('select[data-option-name]')).find((select) => {
      return String(select.getAttribute('data-option-name')).toLowerCase() === 'size';
    });

    if (sizeSelect && sizeSelect.selectedOptions && sizeSelect.selectedOptions[0]) {
      erpInput.value = sizeSelect.selectedOptions[0].getAttribute('data-erp-size-value') || '';
      return;
    }

    erpInput.value = '';
  }

  isCustomSizeSelected(root = document) {
    const container = root.querySelector ? root : document;

    return !!container.querySelector('input[data-custom-size-option="true"]:checked');
  }

  toggleCustomSizeFields(root = document) {
    const container = root.querySelector ? root : document;
    const customBox = container.querySelector('[data-custom-size-box]');
    const isCustom = this.isCustomSizeSelected(container);

    if (!customBox) return;

    if (isCustom) {
      customBox.style.display = 'block';
    } else {
      customBox.style.display = 'none';
      this.clearCustomSizeProperties(container);
    }
  }
  rebuildCustomSizeSelects(customBox, unit) {
    if (!customBox) return;

    customBox.querySelectorAll('.custom-size-select, .dress_ul select').forEach((select) => {
      const valuesRaw =
        unit === 'cm'
          ? select.getAttribute('data-cm-values')
          : select.getAttribute('data-inch-values');

      if (!valuesRaw) {
        console.warn('Missing custom size values:', unit, select);
        return;
      }

      const values = valuesRaw
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);

      select.innerHTML = '';

      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = '-- Veuillez sélectionner --';
      select.appendChild(placeholder);

      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        select.appendChild(option);
      });

      select.value = '';
      select.classList.remove('is-error');
    });
  }

  toggleCustomUnit(switchBtn, root = document) {
    const customBox = switchBtn.closest('[data-custom-size-box]');
    if (!customBox) return;

    const currentUnit = switchBtn.classList.contains('right') ? 'cm' : 'inch';
    const nextUnit = currentUnit === 'inch' ? 'cm' : 'inch';

    switchBtn.classList.toggle('left', nextUnit === 'inch');
    switchBtn.classList.toggle('right', nextUnit === 'cm');
    switchBtn.setAttribute('data-current-unit', nextUnit);

    this.rebuildCustomSizeSelects(customBox, nextUnit);

    customBox.querySelectorAll('.unit_item').forEach((item) => {
      if (item.firstChild) {
        item.firstChild.nodeValue = nextUnit === 'cm' ? '(cm)' : '(in)';
      }
    });

    const errorBox = customBox.querySelector('[data-error-message]');
    if (errorBox) errorBox.innerHTML = '';

    this.clearCustomSizeProperties(root);
  }

  validateAndGenerateCustomSize(root = document) {
    const container = root.querySelector ? root : document;
    const form = container.querySelector('form[data-type="add-to-cart-form"]');
    const customBox = container.querySelector('[data-custom-size-box]');
    const errorBox = container.querySelector('[data-error-message]');

    if (!form || !customBox) return true;

    if (!this.isCustomSizeSelected(container)) {
      this.clearCustomSizeProperties(container);
      return true;
    }

    const missing = [];

    const switchBtn = customBox.querySelector('.unit-switch');
    const currentUnit = switchBtn && switchBtn.classList.contains('right') ? 'cm' : 'inch';

    this.setFormProperty(form, 'properties[Unit]', currentUnit);
    this.setFormProperty(form, 'properties[_Size]', 'Custom Size');

    customBox.querySelectorAll('.dress_ul select').forEach((select) => {
      const value = select.value ? select.value.trim() : '';
      const optionName = select.getAttribute('data-option') || 'Measurement';
      const propertyName = select.getAttribute('data-name');

      if (!value) {
        missing.push(optionName);
        select.classList.add('is-error');
      } else {
        select.classList.remove('is-error');
        this.setFormProperty(form, propertyName, value);
      }
    });

    const checkbox = customBox.querySelector('[name="return_support_checkbox"]');
    const notice = customBox.querySelector('.return_support_notice');

    if (!checkbox || !checkbox.checked) {
      missing.push('terms and conditions');
      if (notice) notice.classList.add('is-error');
    } else {
      if (notice) notice.classList.remove('is-error');
    }

    if (missing.length) {
      if (errorBox) {
        errorBox.innerHTML = `<span class="error">Please provide: ${missing.join(', ')}</span>`;
      }

      return false;
    }

    if (errorBox) errorBox.innerHTML = '';

    return true;
  }

  setFormProperty(form, name, value) {
    if (!form || !name) return;

    let input = Array.from(form.querySelectorAll('input[type="hidden"]')).find((item) => {
      return item.getAttribute('name') === name;
    });

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    input.value = value;
  }

  clearCustomSizeProperties(root = document) {
    const container = root.querySelector ? root : document;
    const form = container.querySelector('form[data-type="add-to-cart-form"]');

    if (!form) return;

    const names = [
      'properties[Unit]',
      'properties[Bust]',
      'properties[Waist]',
      'properties[Hips]',
      'properties[Hollow to Floor]',
      'properties[Height (Bare Foot)]',
      'properties[Extra Length]'
    ];

    names.forEach((name) => {
      const input = Array.from(form.querySelectorAll('input[type="hidden"]')).find((item) => {
        return item.getAttribute('name') === name;
      });

      if (input) input.remove();
    });

    const errorBox = container.querySelector('[data-error-message]');
    if (errorBox) errorBox.innerHTML = '';

    container.querySelectorAll('.is-error').forEach((item) => {
      item.classList.remove('is-error');
    });
  }

  updateOptionLabels(root = document) {
    const container = root.querySelector ? root : document;

    container.querySelectorAll('[data-option-name]:checked').forEach((input) => {
      const optionName = input.getAttribute('data-option-name');
      if (!optionName) return;

      const handle = this.handleize(optionName);
      const label = container.querySelector(`.option-name--${handle}`);

      if (label) label.textContent = input.value;
    });

    container.querySelectorAll('select[data-option-name]').forEach((select) => {
      const optionName = select.getAttribute('data-option-name');
      if (!optionName) return;

      const handle = this.handleize(optionName);
      const label = container.querySelector(`.option-name--${handle}`);

      if (label) label.textContent = select.value;
    });
  }

  initDeliveryTime(root = document) {
    const container = root.querySelector ? root : document;
    const shipdate = container.querySelector('#faq-delivery-time');

    if (!shipdate || !shipdate.dataset.start || !shipdate.dataset.end) return;

    const minDate = parseInt(shipdate.dataset.start, 10);
    const maxDate = parseInt(shipdate.dataset.end, 10);

    if (Number.isNaN(minDate) || Number.isNaN(maxDate)) return;

    const month = ["janv", "févr", "mars", "avril", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];

    let newDate = new Date();
    let startDate = null;
    let endDate = null;
    let count = 0;

    while (count < minDate) {
      startDate = new Date(newDate.setDate(newDate.getDate() + 1));

      if (startDate.getDay() !== 0 && startDate.getDay() !== 6) {
        count += 1;
      }
    }

    while (count < maxDate) {
      endDate = new Date(newDate.setDate(newDate.getDate() + 1));

      if (endDate.getDay() !== 0 && endDate.getDay() !== 6) {
        count += 1;
      }
    }

    if (startDate && endDate) {
      shipdate.innerHTML = `${month[startDate.getMonth()]}. ${startDate.getDate()} - ${month[endDate.getMonth()]}. ${endDate.getDate()}`;
    }
  }

  updateDocumentMeta(newDoc) {
    const newTitle = newDoc.querySelector('title');
    if (newTitle) document.title = newTitle.textContent;

    [
      'link[rel="canonical"]',
      'meta[name="description"]',
      'meta[property="og:title"]',
      'meta[property="og:url"]',
      'meta[property="og:image"]'
    ].forEach((selector) => {
      const current = document.querySelector(selector);
      const next = newDoc.querySelector(selector);

      if (current && next) current.replaceWith(next);
    });
  }

  shouldIgnoreOptionName(name) {
    const normalized = String(name).toLowerCase().trim();

    return (
      normalized === 'color' ||
      normalized === 'Couleur' ||
      normalized === 'siblingcolor' ||
      normalized === 'sibling color'
    );
  }

  escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/"/g, '\\"');
  }

  handleize(value) {
    return String(value)
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!window.productFamilyAjax) {
    window.productFamilyAjax = new ProductFamilyAjax();
  }
});